import { translate as translateKey } from '@elements/translations'

/**
 * Translation keys of the search UI. The host page provides the translations in the global `_translations` object
 * (Twig: `jsConfig('_translations')`); they are read with `translate()` of `@elements/translations`.
 */
export const TRANSLATION_KEYS = [
    'agent-search.search.label',
    'agent-search.search.placeholder',
    'agent-search.search.submit',
    'agent-search.search.empty',
    'agent-search.results.loading',
    'agent-search.results.count',
    'agent-search.results.count-one',
    'agent-search.results.count-approx',
    'agent-search.results.none',
    'agent-search.results.error',
    'agent-search.results.rate-limited',
    'agent-search.block.error',
    'agent-search.block.website',
    'agent-search.paging.label',
    'agent-search.paging.prev',
    'agent-search.paging.next',
    'agent-search.paging.page',
] as const

export type TranslationKey = (typeof TRANSLATION_KEYS)[number]

export type Texts = Record<TranslationKey, string>

export type TranslationVariables = Record<string, string | number>

/** Built-in texts, used when a translation is missing. Placeholders use the `{{ name }}` syntax of `@elements/translations`. */
export const en: Texts = {
    'agent-search.search.label': 'What are you looking for?',
    'agent-search.search.placeholder': 'What are you looking for?',
    'agent-search.search.submit': 'Search',
    'agent-search.search.empty': 'Please enter a search term.',
    'agent-search.results.loading': 'Searching …',
    'agent-search.results.count': '{{total}} results',
    'agent-search.results.count-one': '{{total}} result',
    'agent-search.results.count-approx': 'about {{total}} results',
    'agent-search.results.none': 'No results found. Please try another search term.',
    'agent-search.results.error': 'The search is currently unavailable. Please try again later.',
    'agent-search.results.rate-limited': 'There are too many search requests right now. Please try again shortly.',
    'agent-search.block.error': 'This part of the search is currently unavailable.',
    'agent-search.block.website': 'Website',
    'agent-search.paging.label': 'Pagination',
    'agent-search.paging.prev': 'Previous page',
    'agent-search.paging.next': 'Next page',
    'agent-search.paging.page': 'Page {{page}}',
}

export const de: Texts = {
    'agent-search.search.label': 'Wonach suchen Sie?',
    'agent-search.search.placeholder': 'Wonach suchen Sie?',
    'agent-search.search.submit': 'Suchen',
    'agent-search.search.empty': 'Bitte geben Sie einen Suchbegriff ein.',
    'agent-search.results.loading': 'Suche läuft …',
    'agent-search.results.count': '{{total}} Ergebnisse',
    'agent-search.results.count-one': '{{total}} Ergebnis',
    'agent-search.results.count-approx': 'ca. {{total}} Ergebnisse',
    'agent-search.results.none': 'Keine Ergebnisse gefunden. Bitte versuchen Sie einen anderen Suchbegriff.',
    'agent-search.results.error': 'Die Suche ist derzeit nicht verfügbar. Bitte versuchen Sie es später noch einmal.',
    'agent-search.results.rate-limited': 'Gerade sind zu viele Suchanfragen eingegangen. Bitte versuchen Sie es in Kürze erneut.',
    'agent-search.block.error': 'Dieser Teil der Suche ist derzeit nicht verfügbar.',
    'agent-search.block.website': 'Website',
    'agent-search.paging.label': 'Seitennavigation',
    'agent-search.paging.prev': 'Vorherige Seite',
    'agent-search.paging.next': 'Nächste Seite',
    'agent-search.paging.page': 'Seite {{page}}',
}

const languages: Record<string, Texts> = { de, en }

/** The built-in texts for a language (`de-AT` → `de`); English for any other language. */
export function fallbackTexts(lang: string | undefined): Texts {
    return languages[(lang ?? 'en').toLowerCase().slice(0, 2)] ?? en
}

/**
 * `translate()` of `@elements/translations`, falling back to the built-in text of `lang` when the translation is
 * missing or still the untranslated key (Pimcore returns the key for a new, empty translation).
 */
export function translate(key: TranslationKey, lang: string | undefined, variables?: TranslationVariables): string {
    const translated = hostTranslate(key, variables)
    const text = translated && translated !== key ? translated : fallbackTexts(lang)[key]

    // filled here as well: @elements/translations 1.x, which a host may resolve, ignores the variables
    return format(text, variables ?? {})
}

/** Fills `{{ name }}` placeholders like `@elements/translations`; unknown ones stay. */
export function format(text: string, params: TranslationVariables): string {
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => (key in params ? String(params[key]) : `{{${key}}}`))
}

/**
 * Heading of a result block: `agent-search.block.<type>`. Known types use the built-in text as fallback; any other
 * type (e.g. a future `event` block) can be translated by the host and otherwise shows the type itself.
 */
export function blockTitle(type: string, lang: string | undefined): string {
    const key = `agent-search.block.${type}`

    if (isTranslationKey(key)) {
        return translate(key, lang)
    }

    const translated = hostTranslate(key)

    return translated && translated !== key ? translated : type
}

export function isTranslationKey(key: string): key is TranslationKey {
    return (TRANSLATION_KEYS as readonly string[]).includes(key)
}

/** `translate()` of `@elements/translations`, skipped without a `_translations` object (it would warn on every call). */
function hostTranslate(key: string, variables?: TranslationVariables): string | null {
    return typeof globalThis._translations === 'object' && globalThis._translations !== null ? translateKey(key, variables) : null
}
